import React from "react";
import { Image, Pressable, StyleSheet, View } from "react-native";
import { CirclePlay } from "lucide-react";
import CustomText from "../../common/general/CustomText/CustomText";
import { colors, radii, spacing } from "../../../styles/design-system";
import type { ExplorationMediaItem } from "./ExplorationMenu";

interface ExplorationMenuItemProps {
  item: ExplorationMediaItem;
  isHovered: boolean;
  onPress: () => void;
  onHoverIn?: () => void;
  onHoverOut?: () => void;
}

const ExplorationMenuItem: React.FC<ExplorationMenuItemProps> = ({
  item,
  isHovered,
  onPress,
  onHoverIn,
  onHoverOut,
}) => {
  const mediaLabel = item.type === "video" ? "Video" : "Image";

  return (
    <Pressable
      style={[styles.itemRow, isHovered && styles.itemRowHover]}
      onPress={onPress}
      onHoverIn={onHoverIn}
      onHoverOut={onHoverOut}
      accessibilityRole="button"
      accessibilityLabel={`Open ${item.title}`}>
      <View style={styles.itemImageWrapper}>
        <Image source={{ uri: item.thumbnail }} style={styles.itemImage} resizeMode="cover" alt="" />
        {item.type === "video" ? (
          <View style={[styles.itemOverlay, isHovered && styles.itemOverlayHover]}>
            <CirclePlay size={32} color={colors.white} />
          </View>
        ) : null}
      </View>
      <View style={styles.itemText}>
        <CustomText text={item.title} size="s" weight="medium" style={styles.itemTitle} useTranslationText={false} />
        <View style={styles.itemMeta}>
          <CustomText text={mediaLabel} size="xs" style={styles.itemMetaText} useTranslationText={false} />
          <CustomText text="•" size="xs" style={styles.itemMetaDot} useTranslationText={false} />
          <CustomText text={item.metadata} size="xs" style={styles.itemMetaText} useTranslationText={false} />
        </View>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  itemRow: {
    flexDirection: "row",
    gap: 12,
    padding: 12,
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
  },
  itemRowHover: {
    backgroundColor: "rgba(250, 245, 255, 0.5)",
    borderColor: colors.purple[200],
  },
  itemImageWrapper: {
    width: 80,
    height: 80,
    borderRadius: radii.sm,
    overflow: "hidden",
    backgroundColor: colors.grey[100],
  },
  itemImage: {
    width: "100%",
    height: "100%",
  },
  itemOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.2)",
  },
  itemOverlayHover: {
    backgroundColor: "rgba(0, 0, 0, 0.3)",
  },
  itemText: {
    flex: 1,
    minWidth: 0,
    justifyContent: "flex-start",
    gap: 2,
  },
  itemTitle: {
    color: colors.grey[900],
    marginBottom: spacing.xxs,
  },
  itemMeta: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xxs,
  },
  itemMetaText: {
    color: colors.grey[600],
  },
  itemMetaDot: {
    color: colors.grey[600],
  },
});

export default ExplorationMenuItem;
